import * as React from "react"
import { useTranslation } from "react-i18next"

import { VolumeInput, WeightInput } from "Catalog/shared/components/DimensionInput"
import { useUIPreferences } from "Catalog/shared/components/UIPreferencesContext"
import * as StyledTable from "shared/components/Table/Styles.Table"
import useEditor from "shared/hooks/useEditor"
import { IProduct, ISku } from "shared/hubrise/models"
import { isMultiSku } from "shared/hubrise/utils/Product"

import FoldableSection from "../FoldableSection"

import { FieldBarcodes, FieldServings, SkuName, Hint } from "./Styles"

interface DimensionsProps {
  product: IProduct
}

const Dimensions = ({ product }: DimensionsProps): JSX.Element => {
  const { t } = useTranslation()
  const { editor } = useEditor()
  const { dimensionsExpanded: expanded, setDimensionsExpanded: setExpanded } = useUIPreferences()

  const multiSku = isMultiSku(product)

  const handleChange =
    <TField extends keyof ISku>(sku: ISku, field: TField) =>
    (value: ISku[TField]) =>
      editor.sku.update(product.id, sku.id, field, value)

  return (
    <FoldableSection title={t("product.dimensions.title")} expanded={expanded} onToggle={() => setExpanded(!expanded)}>
      <StyledTable.table>
        {product.skus.length > 0 && (
          <thead>
            <tr>
              {multiSku && <StyledTable.th>{t("product.dimensions.name")}</StyledTable.th>}
              <StyledTable.th>{t("product.dimensions.weight")}</StyledTable.th>
              <StyledTable.th>{t("product.dimensions.volume")}</StyledTable.th>
              <StyledTable.th>{t("product.dimensions.nb_servings")}</StyledTable.th>
              <StyledTable.th>{t("product.dimensions.barcodes")}</StyledTable.th>
            </tr>
          </thead>
        )}
        <tbody>
          {product.skus.map((sku) => (
            <tr key={sku.id}>
              {multiSku && (
                <StyledTable.td>
                  <SkuName>{sku.name}</SkuName>
                </StyledTable.td>
              )}

              <StyledTable.td>
                <WeightInput
                  initialValue={sku.weight}
                  placeholder="0 g"
                  defaultUnit="g"
                  onChange={(val) => handleChange(sku, "weight")(val)}
                />
              </StyledTable.td>

              <StyledTable.td>
                <VolumeInput
                  initialValue={sku.volume}
                  placeholder="0 cl"
                  defaultUnit="cl"
                  onChange={(val) => handleChange(sku, "volume")(val)}
                />
              </StyledTable.td>

              <StyledTable.td>
                <FieldServings
                  initialValue={sku.nbServings}
                  onChange={(value: number | null) => handleChange(sku, "nbServings")(value)}
                  debounce
                  step={1}
                  min={1}
                  nullWhenDecreasingBelowMin={true}
                />
              </StyledTable.td>

              <StyledTable.td>
                <FieldBarcodes
                  initialValue={sku.barcodes}
                  onChange={(value: Array<string>) => handleChange(sku, "barcodes")(value)}
                  isMulti
                  onCreateOption={(label: string, cb: (value: string) => void) => cb(label)}
                  validateOptionValue={(value) => {
                    if (!/^\d+$/.test(value)) return t("product.dimensions.barcodes_invalid.digits_only")
                    if (![8, 12, 13].includes(value.length))
                      return t("product.dimensions.barcodes_invalid.length", { count: value.length })
                  }}
                />
              </StyledTable.td>
            </tr>
          ))}
        </tbody>
      </StyledTable.table>

      <Hint>{t("product.dimensions.supported_units")}</Hint>
    </FoldableSection>
  )
}

export default Dimensions
